/**
 * Task 279 / 279B — Client Operating Setup panel ("إعدادات تشغيل المحتوى").
 *
 * Rendered on the client Overview page after the brand-info card. Self-loads
 * via `get-client-operating-setup`; agency users (canEdit = client.manage) get
 * an editable form, client-category users a read-only view. Saves via
 * `upsert-client-operating-setup`. Storage only — it NEVER triggers content
 * generation, creates calendar entries, or implies enforcement.
 *
 * Two sections:
 *   1. Package deliverables — READ-ONLY, derived from the client's active
 *      package assignment (`services_snapshot[].deliverables[]`). This panel is
 *      NOT the source of truth for the agreed work volume; it only displays it,
 *      with a CTA to the Services tab when no package is assigned (Task 279B).
 *   2. Initial scheduling defaults — editable operational starting points
 *      (dates / days / times / timezone / lead+buffer) plus the distribution
 *      platforms. NOT "best times" and NOT a final schedule.
 */

import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { Link } from 'react-router-dom';

import { KmiApiError } from '../../api/client';
import { getClientOperatingSetup, upsertClientOperatingSetup } from '../../api/endpoints';
import type {
  ClientOperatingSetup,
  ClientPackageAssignmentRow,
  SystemCapabilityKey,
} from '../../api/types';
import { EmptyState } from '../../components/states';
import {
  buildOperatingSetupPayload,
  DAY_ITEMS,
  dayLabel,
  deliverableTypeLabelAr,
  formatTimesInput,
  formStateFromRow,
  packageDeliverablesFromAssignment,
  PLATFORM_ITEMS,
  platformLabel,
  serviceCadenceLabelAr,
  TIMEZONE_OPTIONS,
  validateOperatingSetup,
  type OperatingSetupFormState,
} from './operatingSetup';

const PACKAGE_HELP = 'هذه تُحدد من الباقة أو الخدمة المتفق عليها، وليست جدول نشر نهائي.';
// Task 280C — neutral pointer: the monthly counts live in the package editor's
// config (not in the assignment snapshot), so we point there rather than
// claiming none are defined.
const PACKAGE_DELIVERABLES_POINTER = 'تُدار مخرجات الباقة وأعدادها من إعداد الباقة/الخدمات.';
const SCHEDULE_HELP = 'هذه أوقات افتراضية لتوزيع المحتوى ويمكن تعديلها لاحقًا.';

const GRID: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit,minmax(200px,1fr))',
  gap: 14,
};

type LoadState =
  | { readonly status: 'loading' }
  | { readonly status: 'error' }
  | { readonly status: 'ready'; readonly setup: ClientOperatingSetup | null };

export function OperatingSetupSection({
  clientId,
  canEdit,
  activeAssignment,
  capabilities,
}: {
  readonly clientId: string;
  readonly canEdit: boolean;
  readonly activeAssignment: ClientPackageAssignmentRow | null;
  readonly capabilities: ReadonlySet<SystemCapabilityKey>;
}): ReactNode {
  const [state, setState] = useState<LoadState>({ status: 'loading' });
  const [editing, setEditing] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    const ctrl = new AbortController();
    setState({ status: 'loading' });
    getClientOperatingSetup(clientId, { signal: ctrl.signal })
      .then((data) => {
        if (ctrl.signal.aborted) return;
        setState({ status: 'ready', setup: data.operating_setup });
        setEditing(false);
      })
      .catch((err: unknown) => {
        if (ctrl.signal.aborted) return;
        if (err instanceof DOMException && err.name === 'AbortError') return;
        setState({ status: 'error' });
      });
    return () => ctrl.abort();
  }, [clientId, reloadKey]);

  const setup = state.status === 'ready' ? state.setup : null;
  // Agency sees the form when actively editing OR when nothing is configured
  // yet (empty editable form). Clients never see the form.
  const showScheduling = capabilities.has('content') || capabilities.has('publishing');
  const showForm = showScheduling && canEdit && (editing || setup === null);

  return (
    <section id="operating-setup-section" className="card" style={{ padding: 22 }}>
      <div className="sec-title">
        <h2>إعدادات تشغيل المحتوى</h2>
        <span className="meta">client_operating_setup</span>
      </div>

      {state.status === 'loading' ? (
        <p className="tiny">جاري التحميل…</p>
      ) : state.status === 'error' ? (
        <div className="banner banner-error">تعذّر تحميل إعدادات التشغيل. حاول تحديث الصفحة.</div>
      ) : (
        <>
          {/* Package deliverables: always read-only, derived from the active
              package assignment — independent of whether a scheduling-defaults
              row exists yet. */}
          <PackageDeliverablesView clientId={clientId} assignment={activeAssignment} />

          {showForm ? (
            <OperatingSetupForm
              clientId={clientId}
              initial={setup}
              onSaved={(saved) => {
                setState({ status: 'ready', setup: saved });
                setEditing(false);
                setReloadKey((n) => n + 1);
              }}
              onCancel={setup ? () => setEditing(false) : undefined}
            />
          ) : showScheduling && setup ? (
            <OperatingSetupReadView
              setup={setup}
              canEdit={canEdit}
              onEdit={() => setEditing(true)}
            />
          ) : showScheduling ? (
            <>
              <SectionHeading title="إعدادات الجدولة المبدئية" help={SCHEDULE_HELP} />
              <EmptyState
                title="لم يتم ضبط إعدادات الجدولة بعد"
                description="ستظهر هنا إعدادات الجدولة المبدئية ومنصات النشر عندما تضبطها الوكالة."
              />
            </>
          ) : null}
        </>
      )}
    </section>
  );
}

// ── Package deliverables (read-only, package-sourced) ────────────────
//
// Task 279B — the agreed work volume is owned by the client's package/service
// configuration, NOT this panel. We display the active assignment's
// deliverables read-only (mirroring the Services tab) and link there to manage
// them. No editable inputs live here.

function PackageDeliverablesView({
  clientId,
  assignment,
}: {
  readonly clientId: string;
  readonly assignment: ClientPackageAssignmentRow | null;
}): ReactNode {
  const groups = packageDeliverablesFromAssignment(assignment);

  return (
    <div>
      <SectionHeading title="مخرجات الباقة" help={PACKAGE_HELP} />
      {groups.length === 0 ? (
        <div
          style={{
            display: 'flex',
            gap: 10,
            alignItems: 'center',
            flexWrap: 'wrap',
            padding: '12px 14px',
            background: 'var(--cream-2)',
            border: '1px dashed var(--line-2)',
            borderRadius: 'var(--r-md)',
          }}
        >
          <span className="body" style={{ flex: 1, color: 'var(--ink-2)' }}>
            {PACKAGE_DELIVERABLES_POINTER}
          </span>
          <Link
            to={`/app/clients/${clientId}/services`}
            className="btn-text"
            style={{ padding: '6px 10px' }}
          >
            إدارة الخدمات والباقات ←
          </Link>
        </div>
      ) : (
        <ul
          style={{
            listStyle: 'none',
            padding: 0,
            margin: 0,
            display: 'flex',
            flexDirection: 'column',
            gap: 10,
          }}
        >
          {groups.map((g) => (
            <li
              key={g.serviceId}
              style={{
                padding: '10px 12px',
                border: '1px solid var(--line)',
                borderRadius: 'var(--r-md)',
              }}
            >
              <div style={{ fontWeight: 500, marginBottom: 4 }}>{g.serviceName}</div>
              <ul
                style={{
                  listStyle: 'none',
                  padding: 0,
                  margin: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 2,
                }}
              >
                {g.deliverables.map((d, i) => (
                  <li key={i} className="tiny" style={{ color: 'var(--ink-2)' }}>
                    {deliverableTypeLabelAr(d.type)}: {d.label}
                    {d.quantity !== null && d.quantity !== undefined ? ` · ${d.quantity}` : ''}
                    {d.cadence ? ` · ${serviceCadenceLabelAr(d.cadence)}` : ''}
                  </li>
                ))}
              </ul>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// ── Read-only view ───────────────────────────────────────────────────

function OperatingSetupReadView({
  setup,
  canEdit,
  onEdit,
}: {
  readonly setup: ClientOperatingSetup;
  readonly canEdit: boolean;
  readonly onEdit: () => void;
}): ReactNode {
  return (
    <div>
      <SectionHeading title="إعدادات الجدولة المبدئية" help={SCHEDULE_HELP} />
      <div style={GRID}>
        <ReadField label="تاريخ أول نشر" value={setup.first_publish_date} />
        <ReadField
          label="أيام النشر الافتراضية"
          value={(setup.preferred_days ?? []).map(dayLabel).join('، ') || null}
        />
        <ReadField
          label="الأوقات المحلية الافتراضية"
          value={formatTimesInput(setup.preferred_times_local) || null}
        />
        <ReadField label="المنطقة الزمنية" value={setup.timezone} />
        <ReadField label="مهلة الإنتاج (أيام)" value={String(setup.production_lead_time_days)} />
        <ReadField label="مهلة الموافقة (أيام)" value={String(setup.approval_buffer_days)} />
        <ReadField
          label="منصات النشر"
          value={(setup.platforms ?? []).map(platformLabel).join('، ') || null}
        />
      </div>

      {canEdit ? (
        <div style={{ marginTop: 14 }}>
          <button type="button" className="btn-text" onClick={onEdit}>
            تعديل
          </button>
        </div>
      ) : null}
    </div>
  );
}

// ── Edit form ────────────────────────────────────────────────────────

function OperatingSetupForm({
  clientId,
  initial,
  onSaved,
  onCancel,
}: {
  readonly clientId: string;
  readonly initial: ClientOperatingSetup | null;
  readonly onSaved: (saved: ClientOperatingSetup) => void;
  readonly onCancel?: () => void;
}): ReactNode {
  const [form, setForm] = useState<OperatingSetupFormState>(() => formStateFromRow(initial));
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string[]>>({});

  function patch(next: Partial<OperatingSetupFormState>): void {
    setForm((f) => ({ ...f, ...next }));
  }

  async function onSubmit(e: FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    setError(null);
    const errs = validateOperatingSetup(form);
    if (Object.keys(errs).length > 0) {
      setFieldErrors(errs);
      return;
    }
    setFieldErrors({});
    setSubmitting(true);
    try {
      const data = await upsertClientOperatingSetup({
        input: buildOperatingSetupPayload(form, clientId),
      });
      onSaved(data.operating_setup);
    } catch (err) {
      setSubmitting(false);
      handleApiErr(err, setError, setFieldErrors);
    }
  }

  return (
    <form onSubmit={onSubmit} noValidate>
      {error ? (
        <div className="banner banner-error" style={{ marginBottom: 14 }}>
          {error}
        </div>
      ) : null}

      <SectionHeading title="إعدادات الجدولة المبدئية" help={SCHEDULE_HELP} />
      <div style={GRID}>
        <DateField
          id="first_publish_date"
          label="تاريخ أول نشر"
          value={form.firstPublishDate}
          onChange={(v) => patch({ firstPublishDate: v })}
          disabled={submitting}
          error={fieldErrors['first_publish_date']?.[0]}
        />
        <SelectField
          id="timezone"
          label="المنطقة الزمنية"
          value={form.timezone}
          options={TIMEZONE_OPTIONS}
          onChange={(v) => patch({ timezone: v })}
          disabled={submitting}
          error={fieldErrors['timezone']?.[0]}
        />
        <TextField
          id="preferred_times_local"
          label="الأوقات المحلية الافتراضية"
          value={form.preferredTimesText}
          onChange={(v) => patch({ preferredTimesText: v })}
          disabled={submitting}
          placeholder="09:00، 16:00"
          dirLtr
          error={fieldErrors['preferred_times_local']?.[0]}
        />
        <NumField
          id="production_lead_time_days"
          label="مهلة الإنتاج (أيام)"
          value={form.productionLeadTimeDays}
          onChange={(v) => patch({ productionLeadTimeDays: v })}
          disabled={submitting}
          error={fieldErrors['production_lead_time_days']?.[0]}
        />
        <NumField
          id="approval_buffer_days"
          label="مهلة الموافقة (أيام)"
          value={form.approvalBufferDays}
          onChange={(v) => patch({ approvalBufferDays: v })}
          disabled={submitting}
          error={fieldErrors['approval_buffer_days']?.[0]}
        />
      </div>
      <div style={{ marginTop: 12 }}>
        <label className="auth-field-label">أيام النشر الافتراضية</label>
        <ChipRow
          items={DAY_ITEMS}
          selected={form.preferredDays}
          onToggle={(k) => patch({ preferredDays: toggle(form.preferredDays, k) })}
          disabled={submitting}
        />
        <FieldError error={fieldErrors['preferred_days']?.[0]} />
      </div>
      <div style={{ marginTop: 12 }}>
        <label className="auth-field-label">منصات النشر</label>
        <ChipRow
          items={PLATFORM_ITEMS}
          selected={form.platforms}
          onToggle={(k) => patch({ platforms: toggle(form.platforms, k) })}
          disabled={submitting}
        />
        <FieldError error={fieldErrors['platforms']?.[0]} />
      </div>

      <div style={{ display: 'flex', gap: 10, marginTop: 18 }}>
        <button type="submit" className="auth-cta" disabled={submitting} style={{ width: 'auto' }}>
          {submitting ? <span className="spinner" /> : null}
          <span>{submitting ? 'جاري الحفظ…' : 'حفظ إعدادات التشغيل'}</span>
        </button>
        {onCancel ? (
          <button
            type="button"
            className="btn-text"
            onClick={onCancel}
            disabled={submitting}
            style={{ padding: '10px 14px' }}
          >
            إلغاء
          </button>
        ) : null}
      </div>
    </form>
  );
}

// ── Small presentational primitives ──────────────────────────────────

function SectionHeading({
  title,
  help,
}: {
  readonly title: string;
  readonly help: string;
}): ReactNode {
  return (
    <div style={{ marginTop: 18, marginBottom: 6 }}>
      <h3 style={{ margin: 0 }}>{title}</h3>
      <p className="tiny" style={{ marginTop: 4 }}>
        {help}
      </p>
    </div>
  );
}

function ReadField({
  label,
  value,
}: {
  readonly label: string;
  readonly value: string | null | undefined;
}): ReactNode {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <span className="lbl">{label}</span>
      <span className="body">{value && value.length > 0 ? value : '—'}</span>
    </div>
  );
}

function FieldError({ error }: { readonly error?: string }): ReactNode {
  if (!error) return null;
  return (
    <div className="tiny" style={{ color: 'var(--rose)', marginTop: 4 }}>
      {error}
    </div>
  );
}

interface InputCellProps {
  readonly id: string;
  readonly label: string;
  readonly value: string;
  readonly onChange: (v: string) => void;
  readonly disabled?: boolean;
  readonly placeholder?: string;
  readonly dirLtr?: boolean;
  readonly error?: string;
}

function BaseInput(
  props: InputCellProps & { readonly type: 'text' | 'number' | 'date' },
): ReactNode {
  const errorId = props.error ? `${props.id}_error` : undefined;
  return (
    <div className="auth-field" style={{ marginBottom: 0 }}>
      <label className="auth-field-label" htmlFor={props.id}>
        {props.label}
      </label>
      <input
        id={props.id}
        className="auth-input"
        type={props.type}
        inputMode={props.type === 'number' ? 'numeric' : undefined}
        min={props.type === 'number' ? 0 : undefined}
        value={props.value}
        onChange={(e) => props.onChange(e.currentTarget.value)}
        placeholder={props.placeholder}
        disabled={props.disabled}
        dir={props.dirLtr ? 'ltr' : undefined}
        aria-invalid={props.error ? true : undefined}
        aria-describedby={errorId}
        style={{ paddingInlineStart: 18 }}
      />
      {props.error ? (
        <div id={errorId} className="tiny" style={{ color: 'var(--rose)', marginTop: 4 }}>
          {props.error}
        </div>
      ) : null}
    </div>
  );
}

function NumField(props: InputCellProps): ReactNode {
  return <BaseInput {...props} type="number" />;
}
function TextField(props: InputCellProps): ReactNode {
  return <BaseInput {...props} type="text" />;
}
function DateField(props: InputCellProps): ReactNode {
  return <BaseInput {...props} type="date" dirLtr />;
}

// Task 280K — timezone is a curated select (no more free-text + "required"
// error). A loaded value outside the curated list still shows via a fallback
// option, so existing rows with an uncommon timezone are never silently dropped.
function SelectField(props: {
  readonly id: string;
  readonly label: string;
  readonly value: string;
  readonly options: ReadonlyArray<{ readonly value: string; readonly label: string }>;
  readonly onChange: (v: string) => void;
  readonly disabled?: boolean;
  readonly error?: string;
}): ReactNode {
  const errorId = props.error ? `${props.id}_error` : undefined;
  const inList = props.options.some((o) => o.value === props.value);
  return (
    <div className="auth-field" style={{ marginBottom: 0 }}>
      <label className="auth-field-label" htmlFor={props.id}>
        {props.label}
      </label>
      <select
        id={props.id}
        className="auth-input"
        value={props.value}
        onChange={(e) => props.onChange(e.currentTarget.value)}
        disabled={props.disabled}
        dir="ltr"
        aria-invalid={props.error ? true : undefined}
        aria-describedby={errorId}
        style={{ paddingInlineStart: 18 }}
      >
        {inList ? null : <option value={props.value}>{props.value || '—'}</option>}
        {props.options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label} ({o.value})
          </option>
        ))}
      </select>
      {props.error ? (
        <div id={errorId} className="tiny" style={{ color: 'var(--rose)', marginTop: 4 }}>
          {props.error}
        </div>
      ) : null}
    </div>
  );
}

function ChipRow<T extends string | number>({
  items,
  selected,
  onToggle,
  disabled,
}: {
  readonly items: ReadonlyArray<{ readonly key: T; readonly label: string }>;
  readonly selected: ReadonlyArray<T>;
  readonly onToggle: (key: T) => void;
  readonly disabled?: boolean;
}): ReactNode {
  return (
    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 6 }}>
      {items.map((it) => {
        const on = selected.includes(it.key);
        return (
          <button
            key={String(it.key)}
            type="button"
            className={`chip${on ? ' on' : ''}`}
            aria-pressed={on}
            disabled={disabled}
            onClick={() => onToggle(it.key)}
          >
            {on ? '✓ ' : ''}
            {it.label}
          </button>
        );
      })}
    </div>
  );
}

function toggle<T>(arr: ReadonlyArray<T>, key: T): T[] {
  return arr.includes(key) ? arr.filter((x) => x !== key) : [...arr, key];
}

function handleApiErr(
  err: unknown,
  setError: (v: string | null) => void,
  setFieldErrors: (v: Record<string, string[]>) => void,
): void {
  if (err instanceof KmiApiError) {
    if (err.code === 'validation_error' && err.body.fields) {
      const next: Record<string, string[]> = {};
      for (const [k, v] of Object.entries(err.body.fields)) next[k] = [...v];
      setFieldErrors(next);
      return;
    }
    if (err.code === 'permission_denied') {
      setError('لا تملك صلاحية حفظ إعدادات التشغيل.');
      return;
    }
    if (err.code === 'not_found') {
      setError('العميل غير متاح أو تمت أرشفته.');
      return;
    }
    if (err.code === 'unauthenticated') {
      setError('انتهت الجلسة. سيتم إعادة تسجيل الدخول…');
      return;
    }
  }
  setError('تعذّر حفظ الإعدادات. حاول مجدداً.');
}
